import { useEffect, useState } from 'react';
import { RTDBPath, type GroupId } from 'balus-common';
import { trpcClient } from '@framework/trpcClient';
import { RefBuilder } from '@framework/repository';

type Counts = { member: number; guest: number };
type CountsState = { groupId: GroupId; counts: Counts | undefined; failed: boolean };

export const useGroupMemberCount = (groupId: GroupId): { counts: Counts | undefined; failed: boolean } => {
  const [state, setState] = useState<CountsState | null>(null);

  useEffect(() => {
    setState(null);
    let active = true;
    let latestRequestId = 0;
    const ref = RefBuilder.ref(RTDBPath.Group.memberRolesPath(groupId));
    const handleFailure = (error: unknown) => {
      console.error(error);
      setState((current) => ({
        groupId,
        counts: current?.groupId === groupId ? current.counts : undefined,
        failed: true,
      }));
    };
    const callback = () => {
      const requestId = ++latestRequestId;
      trpcClient.group.getGroupMemberAndGuestCount.query({ groupId }).then(
        ({ memberCount, guestCount }) => {
          if (!active || requestId !== latestRequestId) return;
          setState({ groupId, counts: { member: memberCount, guest: guestCount }, failed: false });
        },
        (error: unknown) => {
          if (!active || requestId !== latestRequestId) return;
          handleFailure(error);
        }
      );
    };
    ref.on('value', callback, (error) => {
      if (!active) return;
      ++latestRequestId;
      handleFailure(error);
    });
    return () => {
      active = false;
      ref.off('value', callback);
    };
  }, [groupId]);

  const current = state?.groupId === groupId ? state : null;
  return { counts: current?.counts, failed: current?.failed ?? false };
};
